import React, {
    useMemo, useRef, FC,
} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {DocumentStatusChangeModal, IDocumentStatusChangeModalRef} from 'components/documents/document-status-change-modal';
import {EMetaActionType} from 'entities/metadata';
import {DATA_MODULE, selectEntityData} from 'modules/data';
import {
    actionRequestData,
    createActionHandlerDownloadFile,
    updateDocumentsStatus,
    sendSelectedRowIds,
} from 'modules/data/data-actions';
import {EPropertyCode, ITableEntityData} from 'modules/data/data-types';
import {EEntityType} from 'shared/constants/entities';
import {ECommonMessages} from 'shared/constants/messages';
import {ERequestType} from 'shared/types/requests';
import {showMessage} from 'shared/utils';
import {fetchProperty} from 'shared/utils/request';
import {TAppState} from 'store/config/types';

import {ActionButton} from '../action-button';
import {ITableActionProps} from '../table-action-types';
import {ACTIONS_CODES_FOR_COMMENT_MODAL, ACTIONS_CODES_FOR_SENDING_ROW_IDS} from './data-action-documents-constants';

const ButtonsActionDocumentsComponent: FC<ITableActionProps> = ({
    wrapper,
    meta,
    entityName,
    closeTableSubmenu,
    ...props
}: ITableActionProps) => {
    const dispatch = useDispatch();
    const statusChangeModalRef = useRef<IDocumentStatusChangeModalRef>(null);
    const {selectedRowKeys = []} = useSelector(
        (state: TAppState) => (
            selectEntityData(entityName, EEntityType.TABLE)(state) || {}
        ) as ITableEntityData,
    );
    const documentData = useSelector(
        (state: TAppState) => state[DATA_MODULE]?.[entityName]?.[EEntityType.TABLE]?.rows,
    );

    const selectedRowKeysStrings = useMemo(() => selectedRowKeys.map((row: number | string) => String(row)),
        [selectedRowKeys]);
    const selectedDocuments = useMemo(
        () => documentData?.filter(document => selectedRowKeysStrings.includes(String(document.id))) || [],
        [documentData, selectedRowKeysStrings],
    );

    const isStatusChange = meta?.actionType === EMetaActionType.UPDATE_DOCUMENTS_STATUS;
    const isPublishList = meta?.actionType === EMetaActionType.PUBLISH_LIST;

    const handleClick = async () => {
        const idsList: string[] = [];

        if (selectedRowKeys?.length === 0) {
            showMessage({message: ECommonMessages.SELECT_DOCUMENT, isError: true});
        }
        if (!!selectedRowKeys?.length && meta?.referenceUrl) {
            if (isStatusChange) {
                if (ACTIONS_CODES_FOR_COMMENT_MODAL.includes(meta?.actionCode)) {
                    closeTableSubmenu?.();
                    statusChangeModalRef.current?.openModal();
                } else if (ACTIONS_CODES_FOR_SENDING_ROW_IDS.includes(meta?.actionCode)) {
                    dispatch(
                        sendSelectedRowIds({
                            entityName,
                            referenceUrl: meta.referenceUrl,
                            requestType: meta.requestType ?? ERequestType.POST,
                            selectedRowIds: {ids: selectedRowKeys},
                        }),
                    );
                } else {
                    const requestData = selectedRowKeysStrings.map(id => ({
                        docId: id,
                    }));

                    dispatch(
                        updateDocumentsStatus(
                            entityName, meta.referenceUrl, requestData, meta?.actionCode, meta?.requestType,
                        ),
                    );
                }
            } else if (meta?.actionType === EMetaActionType.PUBLISH_LIST) {
                const requiredEDS = await fetchProperty(EPropertyCode?.REQUIRED_EDS_WHEN_DOC_PUBLISH)
                    .then(res => res?.data !== 'N');
                const requiredLoaSign = await fetchProperty(EPropertyCode?.REQUIRED_LOA_SGN_WHEN_DOC_PUBLISH)
                    .then(res => res?.data !== 'N');

                const isSigned = (fileList: any): boolean => !!fileList[0]?.signatureFileName;
                const hasLoaSign = (fileList: any): boolean => !!fileList[0]?.loaSignFileName;

                selectedDocuments.forEach((item: any) => {
                    const isDocumentSigned = isSigned(item?.fileList);
                    const hasDocumentLoaSign = hasLoaSign(item?.fileList);

                    if (requiredEDS && !isDocumentSigned && requiredLoaSign && !hasDocumentLoaSign) {
                        showMessage({message: `Документ: ${item.docName} не подписан и не имеет МЧД`, isError: true});
                    } else if (requiredEDS && !isDocumentSigned) {
                        showMessage({message: `Документ: ${item.docName} не подписан`, isError: true});
                    } else if (requiredLoaSign && !hasDocumentLoaSign) {
                        showMessage({message: `Документ: ${item.docName} не имеет МЧД`, isError: true});
                    } else {
                        idsList.push(item?.id);
                    }
                });

                if (idsList?.length === selectedDocuments.length) {
                    closeTableSubmenu?.();
                    statusChangeModalRef.current?.openModal();
                }
            } else if (meta?.actionType === EMetaActionType.DOWNLOAD_ZIP_TABLE_ROWS) {
                dispatch(
                    createActionHandlerDownloadFile(
                        entityName,
                        meta.referenceUrl,
                        meta.requestType || ERequestType.POST,
                        {ids: [...selectedRowKeys]},
                    ),
                );
            } else { // дефолтное поведение для остальных кнопок - просто отправить запрос
                actionRequestData({
                    requestUrl: meta.referenceUrl,
                    requestType: meta.requestType ?? ERequestType.POST,
                    data: [...selectedRowKeysStrings],
                    options: {useRawData: true},
                });
            }
        }
    };

    const Component = wrapper || ActionButton;
    return (
        <>
            <Component
                {...props}
                onClick={handleClick}
                actionIcon={meta.actionIcon}
                disabled={!selectedRowKeys.length}
            >
                {meta.actionTitle}
            </Component>
            {(isStatusChange || isPublishList) && (
                <DocumentStatusChangeModal
                    ref={statusChangeModalRef}
                    meta={meta}
                    entityName={entityName}
                    selectedDocuments={selectedDocuments}
                />
            )}
        </>
    );
};

export const ButtonsActionDocuments = ButtonsActionDocumentsComponent;
